import { useRef, useState } from 'react';
import { Button, Snackbar, Div, Card, Headline, Text } from '@vkontakte/vkui';
import { Icon28ShareOutline, Icon28CopyOutline } from '@vkontakte/icons';
import {
  InviteShare, buildInviteText, copyText, tryNativeShare,
  telegramShareUrl, whatsappShareUrl, vkShareUrl, viberShareUrl, maxShareUrl,
  shareToVK, canWebShare,
} from '../api/share';

interface Props {
  invite: InviteShare;
  size?: 's' | 'm' | 'l';
  mode?: 'primary' | 'secondary' | 'outline';
  stretched?: boolean;
}

const LINK_STYLE: React.CSSProperties = {
  display: 'block',
  padding: '14px 8px',
  color: 'var(--vkui--color_text)',
  textDecoration: 'none',
  borderBottom: '1px solid var(--vkui--color_separator)',
  fontSize: 16,
};

export default function ShareInviteButton({ invite, size = 's', mode = 'secondary', stretched = false }: Props) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [snack, setSnack] = useState<string | null>(null);
  const lastFire = useRef(0);
  const text = buildInviteText(invite);

  async function onShare() {
    if (canWebShare()) {
      const ok = await tryNativeShare(invite);
      if (ok) return;
    }
    setMenuOpen(true);
  }

  async function copyOnly(label = 'Скопировано') {
    const now = Date.now();
    if (now - lastFire.current < 500) return;
    lastFire.current = now;
    const ok = await copyText(text);
    setSnack(ok ? label : 'Не удалось скопировать');
    setMenuOpen(false);
  }

  return (
    <>
      <Button
        size={size}
        mode={mode}
        stretched={stretched}
        before={<Icon28ShareOutline />}
        onClick={onShare}
      >
        Поделиться
      </Button>

      {menuOpen && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 10000,
            background: 'rgba(0,0,0,0.45)',
            display: 'flex',
            alignItems: 'flex-end',
            justifyContent: 'center',
          }}
          onClick={() => setMenuOpen(false)}
        >
          <Card
            style={{
              width: '100%',
              maxWidth: 480,
              margin: 12,
              padding: 12,
              background: 'var(--vkui--color_background_content)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <Headline level="2" style={{ marginBottom: 8 }}>
              {invite.title ? `Приглашение в «${invite.title}»` : 'Поделиться приглашением'}
            </Headline>
            <Text style={{ marginBottom: 8, fontSize: 13, color: 'var(--vkui--color_text_secondary)', wordBreak: 'break-all' }}>
              Код: {invite.code}
            </Text>

            {/* Real <a> links — VK webview allows navigation on real anchors */}
            <Div
              style={{ padding: '14px 8px', cursor: 'pointer', borderBottom: '1px solid var(--vkui--color_separator)', fontWeight: 600 }}
              onClick={async () => {
                const now = Date.now();
                if (now - lastFire.current < 600) return;
                lastFire.current = now;
                setMenuOpen(false);
                await shareToVK(text, invite.link);
              }}
            >
              ВКонтакте
            </Div>
            <a style={LINK_STYLE} href={telegramShareUrl(text)} target="_blank" rel="noopener noreferrer">
              Telegram
            </a>
            <a style={LINK_STYLE} href={whatsappShareUrl(text)} target="_blank" rel="noopener noreferrer">
              WhatsApp
            </a>
            <a style={LINK_STYLE} href={vkShareUrl(text, invite.link)} target="_blank" rel="noopener noreferrer">
              VK (веб-диалог)
            </a>
            <a
              style={LINK_STYLE}
              href={maxShareUrl(text)}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => {
                void copyText(text);
                setSnack('Текст скопирован — вставьте в MAX');
              }}
            >
              MAX (текст скопируется)
            </a>
            <a style={LINK_STYLE} href={viberShareUrl(text)} target="_blank" rel="noopener noreferrer">
              Viber
            </a>

            <Div
              style={{ padding: '14px 8px', cursor: 'pointer', borderBottom: '1px solid var(--vkui--color_separator)' }}
              onClick={() => void copyOnly('Скопировано')}
            >
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                <Icon28CopyOutline /> Скопировать текст
              </span>
            </Div>

            {canWebShare() && (
              <Div
                style={{ padding: '14px 8px', cursor: 'pointer', borderBottom: '1px solid var(--vkui--color_separator)' }}
                onClick={async () => {
                  setMenuOpen(false);
                  await tryNativeShare(invite);
                }}
              >
                Системное меню «Поделиться»…
              </Div>
            )}

            <Button
              stretched
              mode="secondary"
              size="m"
              style={{ marginTop: 12 }}
              onClick={() => setMenuOpen(false)}
            >
              Закрыть
            </Button>
          </Card>
        </div>
      )}

      {snack && (
        <Snackbar onClose={() => setSnack(null)} onClosed={() => setSnack(null)}>
          {snack}
        </Snackbar>
      )}
    </>
  );
}
